import React, { useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { EDITOR_COLORS, editorText } from '../styles/editorTheme';

interface InfoTipProps {
  /** Explanatory text shown in the popup. */
  text: string;
  /** Notified when the popup opens/closes, so the parent row can lift itself above later rows. */
  onVisibleChange?: (visible: boolean) => void;
}

/**
 * Small "i" icon that shows an explanatory popup — on hover (web) or on tap (mobile).
 * Place it directly after a label.
 */
export const InfoTip: React.FC<InfoTipProps> = ({ text, onVisibleChange }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    onVisibleChange?.(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  const isWeb = Platform.OS === 'web';

  return (
    <View style={styles.wrap}>
      <Pressable
        style={styles.icon}
        accessibilityRole="button"
        accessibilityLabel={text}
        {...(isWeb
          ? ({ onHoverIn: () => setVisible(true), onHoverOut: () => setVisible(false) } as any)
          : {})}
        onPress={() => setVisible((current) => !current)}
        onBlur={() => setVisible(false)}
      >
        <Text style={styles.iconText}>i</Text>
      </Pressable>
      {visible ? (
        <View style={styles.popup} pointerEvents="none">
          <Text style={styles.popupText}>{text}</Text>
        </View>
      ) : null}
    </View>
  );
};

const ICON_SIZE = 16;

const styles = StyleSheet.create({
  wrap: {
    marginLeft: 6,
    justifyContent: 'center',
    zIndex: 1000,
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: ICON_SIZE / 2,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.placeholder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconText: {
    ...editorText,
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 13,
    color: EDITOR_COLORS.textMuted,
  },
  popup: {
    position: 'absolute',
    // To the right of the icon, top-aligned with it.
    top: -4,
    left: ICON_SIZE + 8,
    width: 240,
    paddingVertical: 6,
    paddingHorizontal: 9,
    borderRadius: 5,
    backgroundColor: 'rgba(255, 255, 255, 1)',
    opacity: 1,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 6,
    zIndex: 100,
  },
  popupText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
});
